import { motion } from "framer-motion";
import { PhoneCall, Bot, Waves, BarChart3, Inbox, RefreshCw, ChevronDown, Cpu } from "lucide-react";

const LAYERS = [
  {
    tag: "Core AI Workforce",
    note: "They act on every patient — inbound and outbound.",
    color: "text-brand-engagement",
    items: [
      { icon: PhoneCall, name: "AI Receptionist", desc: "Answers & books calls 24/7" },
      { icon: Bot, name: "Copilot", desc: "Proactive SMS, email & social" },
    ],
  },
  {
    tag: "Clinic OS Intelligence",
    note: "The brain — learns from every conversation and metric.",
    color: "text-brand-insights",
    items: [
      { icon: Waves, name: "Call IQ", desc: "Intent, notes & patient insight" },
      { icon: BarChart3, name: "Clinic Insights", desc: "Real-time performance signals" },
    ],
  },
  {
    tag: "Data Fabric",
    note: "The connective layer — one record, always in sync.",
    color: "text-brand-operations",
    items: [
      { icon: Inbox, name: "Unified Inbox", desc: "Every channel, one thread" },
      { icon: RefreshCw, name: "EMR Bridge", desc: "2-way EMR sync" },
    ],
  },
];

export const ClinicOSIntelligence = () => (
  <section data-testid="clinic-os-intelligence-section" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-3xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-500">How Clinic OS works</p>
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
          One brain behind your <span className="text-gradient">entire clinic</span>
        </h2>
        <p className="mt-5 text-lg text-muted-foreground">
          Your AI Receptionist and Copilot never work blind. Every action is powered by Clinic OS
          Intelligence — learning from your conversations and clinic data, all on one connected record.
        </p>
      </div>

      <div className="relative mx-auto mt-16 max-w-3xl">
        <div className="absolute -top-3 left-1/2 z-10 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-violet-500/30 bg-card px-3 py-1 text-xs font-bold shadow-sm">
          <Cpu className="h-3.5 w-3.5 text-violet-500" /> Clinic OS
        </div>
        <div className="space-y-2 rounded-3xl border border-border bg-card p-5 sm:p-7">
          {LAYERS.map((layer, i) => (
            <div key={layer.tag}>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="rounded-2xl border border-border bg-secondary/40 p-4"
                data-testid={`os-layer-${i}`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="sm:w-48 sm:flex-none">
                    <div className={`text-xs font-bold uppercase tracking-wide ${layer.color}`}>{layer.tag}</div>
                    <p className="mt-1 text-xs text-muted-foreground">{layer.note}</p>
                  </div>
                  <div className="grid flex-1 grid-cols-1 gap-2.5 sm:grid-cols-2">
                    {layer.items.map((it) => (
                      <div key={it.name} className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
                        <span className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-secondary ${layer.color}`}>
                          <it.icon className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{it.name}</div>
                          <div className="truncate text-xs text-muted-foreground">{it.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
              {i < LAYERS.length - 1 && (
                <div className="flex justify-center py-1.5">
                  <ChevronDown className="h-5 w-5 text-violet-500/50" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
